import React, { useEffect, useState } from "react";
import api from "../../services/api";
import AdminPageHeader, { AdminButton } from "../../components/admin/AdminPageHeader";
import { SectionCard } from "../../components/admin/AdminCard";
import toast from "react-hot-toast";

type LifePackage = {
  id: string;
  qty: number;
  price_cents: number;
  currency: string;
  active: boolean;
  sort_order: number;
};

type Draft = {
  id: string | null;
  qty: string;
  price: string; // major units, as typed
  currency: string;
  active: boolean;
  sort_order: string;
};

const EMPTY: Draft = { id: null, qty: "5", price: "", currency: "ron", active: true, sort_order: "0" };

/**
 * Life packages sold in the app's Vieți shop.
 *
 * The shop needs at least one ACTIVE package to open at all — with an empty
 * table it reports itself unavailable regardless of the platform toggle, which
 * is exactly the state it shipped in, since nothing ever created a package.
 */
export default function LifePackages() {
  const [packages, setPackages] = useState<LifePackage[]>([]);
  const [hardCap, setHardCap] = useState(30);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [pendingDelete, setPendingDelete] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get<{ packages: LifePackage[]; hard_cap: number }>("/admin/life-packages");
      setPackages(data.packages || []);
      if (data.hard_cap) setHardCap(data.hard_cap);
    } catch (e: any) {
      toast.error(e?.response?.data?.error || "Nu s-au putut încărca pachetele.");
    }
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, []);

  const money = (cents: number, currency: string) =>
    `${(cents / 100).toLocaleString("ro-RO", { minimumFractionDigits: cents % 100 ? 2 : 0 })} ${currency.toUpperCase()}`;

  const openNew = () => setDraft({ ...EMPTY, sort_order: String(packages.length) });
  const openEdit = (p: LifePackage) =>
    setDraft({
      id: p.id,
      qty: String(p.qty),
      price: String(p.price_cents / 100),
      currency: p.currency,
      active: p.active,
      sort_order: String(p.sort_order),
    });

  const save = async () => {
    if (!draft) return;
    const qty = parseInt(draft.qty, 10);
    const price = Number(String(draft.price).replace(",", "."));
    if (!Number.isFinite(qty) || qty < 1 || qty > hardCap) {
      toast.error(`Numărul de vieți trebuie să fie între 1 și ${hardCap}.`);
      return;
    }
    if (!Number.isFinite(price) || price < 0) {
      toast.error("Prețul trebuie să fie un număr pozitiv.");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        qty,
        // The API works in bani; rounding here avoids 1999.9999 from a float.
        price_cents: Math.round(price * 100),
        currency: draft.currency.trim().toLowerCase(),
        active: draft.active,
        sort_order: parseInt(draft.sort_order, 10) || 0,
      };
      if (draft.id) await api.put(`/admin/life-packages/${draft.id}`, payload);
      else await api.post("/admin/life-packages", payload);
      toast.success(draft.id ? "Pachet actualizat." : "Pachet adăugat.");
      setDraft(null);
      await load();
    } catch (e: any) {
      toast.error(e?.response?.data?.error || "Nu s-a putut salva pachetul.");
    }
    setSaving(false);
  };

  const toggleActive = async (p: LifePackage) => {
    try {
      await api.put(`/admin/life-packages/${p.id}`, { active: !p.active });
      setPackages((prev) => prev.map((x) => (x.id === p.id ? { ...x, active: !x.active } : x)));
    } catch (e: any) {
      toast.error(e?.response?.data?.error || "Nu s-a putut schimba starea pachetului.");
    }
  };

  const remove = async (id: string) => {
    try {
      await api.delete(`/admin/life-packages/${id}`);
      setPackages((prev) => prev.filter((x) => x.id !== id));
      toast.success("Pachet șters.");
    } catch (e: any) {
      toast.error(e?.response?.data?.error || "Nu s-a putut șterge pachetul.");
    }
    setPendingDelete(null);
  };

  const activeCount = packages.filter((p) => p.active).length;

  return (
    <div className="space-y-6">
      <AdminPageHeader
        title="Pachete de vieți"
        subtitle="Pachetele pe care părinții și sportivii le pot cumpăra din aplicație."
        actions={<AdminButton onClick={openNew}>Adaugă pachet</AdminButton>}
      />

      {!loading && activeCount === 0 && (
        <div className="px-4 py-3 text-sm border rounded-xl bg-amber-50 border-amber-200 text-amber-800">
          Niciun pachet activ — magazinul de vieți rămâne închis în aplicație până când există cel puțin unul.
        </div>
      )}

      <SectionCard title="Pachete" bodyPadded>
        {loading ? (
          <p className="text-sm text-slate-500">Se încarcă...</p>
        ) : packages.length === 0 ? (
          <p className="text-sm text-slate-500">Niciun pachet definit încă.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs font-semibold tracking-wide uppercase text-slate-400">
                  <th className="py-2 pr-4 text-left">Vieți</th>
                  <th className="py-2 pr-4 text-left">Preț</th>
                  <th className="py-2 pr-4 text-left">Preț / viață</th>
                  <th className="py-2 pr-4 text-left">Ordine</th>
                  <th className="py-2 pr-4 text-left">Stare</th>
                  <th className="py-2 text-right">Acțiuni</th>
                </tr>
              </thead>
              <tbody>
                {packages.map((p) => (
                  <tr key={p.id} className="border-t border-slate-100">
                    <td className="py-3 pr-4 font-bold text-slate-800 tabular-nums">{p.qty}</td>
                    <td className="py-3 pr-4 text-slate-700 tabular-nums">{money(p.price_cents, p.currency)}</td>
                    <td className="py-3 pr-4 text-slate-500 tabular-nums">
                      {money(Math.round(p.price_cents / p.qty), p.currency)}
                    </td>
                    <td className="py-3 pr-4 text-slate-500 tabular-nums">{p.sort_order}</td>
                    <td className="py-3 pr-4">
                      <button
                        type="button"
                        onClick={() => toggleActive(p)}
                        className={`px-2.5 py-1 rounded-full text-xs font-bold ${
                          p.active ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"
                        }`}
                      >
                        {p.active ? "Activ" : "Inactiv"}
                      </button>
                    </td>
                    <td className="py-3 text-right whitespace-nowrap">
                      <button
                        type="button"
                        onClick={() => openEdit(p)}
                        className="px-3 py-1.5 text-xs font-semibold text-indigo-600 hover:text-indigo-700"
                      >
                        Editează
                      </button>
                      {pendingDelete === p.id ? (
                        <>
                          <button
                            type="button"
                            onClick={() => remove(p.id)}
                            className="px-3 py-1.5 text-xs font-bold text-white bg-rose-600 rounded-lg"
                          >
                            Confirmi?
                          </button>
                          <button
                            type="button"
                            onClick={() => setPendingDelete(null)}
                            className="px-2 py-1.5 text-xs text-slate-500"
                          >
                            Nu
                          </button>
                        </>
                      ) : (
                        <button
                          type="button"
                          onClick={() => setPendingDelete(p.id)}
                          className="px-3 py-1.5 text-xs font-semibold text-slate-400 hover:text-rose-600"
                        >
                          Șterge
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </SectionCard>

      {draft && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="w-full max-w-md p-5 space-y-4 bg-white rounded-2xl">
            <h2 className="text-lg font-bold text-slate-800">
              {draft.id ? "Editează pachetul" : "Pachet nou"}
            </h2>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block mb-1 text-sm font-medium text-slate-700">Vieți</label>
                <input
                  type="number"
                  min={1}
                  max={hardCap}
                  value={draft.qty}
                  onChange={(e) => setDraft({ ...draft, qty: e.target.value })}
                  className="w-full px-3 py-2 text-sm border rounded-lg border-slate-300 bg-white text-slate-800 placeholder-slate-400"
                />
                <p className="mt-1 text-xs text-slate-400">Maxim {hardCap} — plafonul unui cont.</p>
              </div>
              <div>
                <label className="block mb-1 text-sm font-medium text-slate-700">Preț</label>
                <input
                  type="text"
                  inputMode="decimal"
                  placeholder="19.99"
                  value={draft.price}
                  onChange={(e) => setDraft({ ...draft, price: e.target.value })}
                  className="w-full px-3 py-2 text-sm border rounded-lg border-slate-300 bg-white text-slate-800 placeholder-slate-400"
                />
              </div>
              <div>
                <label className="block mb-1 text-sm font-medium text-slate-700">Monedă</label>
                <input
                  type="text"
                  maxLength={3}
                  value={draft.currency}
                  onChange={(e) => setDraft({ ...draft, currency: e.target.value })}
                  className="w-full px-3 py-2 text-sm uppercase border rounded-lg border-slate-300 bg-white text-slate-800 placeholder-slate-400"
                />
              </div>
              <div>
                <label className="block mb-1 text-sm font-medium text-slate-700">Ordine</label>
                <input
                  type="number"
                  value={draft.sort_order}
                  onChange={(e) => setDraft({ ...draft, sort_order: e.target.value })}
                  className="w-full px-3 py-2 text-sm border rounded-lg border-slate-300 bg-white text-slate-800 placeholder-slate-400"
                />
              </div>
            </div>

            <label className="flex items-center gap-2 text-sm text-slate-700">
              <input
                type="checkbox"
                checked={draft.active}
                onChange={(e) => setDraft({ ...draft, active: e.target.checked })}
                className="w-4 h-4 rounded border-slate-300"
              />
              Activ (vizibil în magazin)
            </label>

            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setDraft(null)}
                disabled={saving}
                className="flex-1 py-2.5 text-sm font-semibold border rounded-lg border-slate-300 text-slate-600 disabled:opacity-60"
              >
                Anulează
              </button>
              <button
                type="button"
                onClick={save}
                disabled={saving}
                className="flex-1 py-2.5 text-sm font-bold text-white bg-indigo-600 rounded-lg disabled:opacity-60"
              >
                {saving ? "Se salvează..." : "Salvează"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
